import { T, useT } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';
import { useEffect, useRef, useState } from 'react';

import { logError, trackEvent } from '@/modules/analytics';
import { updateUserHealthConditions } from '@/modules/api';
import { useOnboardingSteps, useRulesEngine } from '@/modules/rulesEngine';
import {
  CtaButton,
  Screen,
  ScreenHeading,
  SelectableRow,
  SelectableRowSkeleton,
} from '@/modules/ui';

import { CheckboxCardContent } from './components/CheckboxCardContent';
import { useHealthConditions } from './hooks/useHealthConditions';

/**
 * "Do you have any of these health conditions?" — a multi-select reached from
 * the eligible screen. Mirrors stylos's HealthConditionsPage (alphabetical
 * list, mutually-exclusive "None of these") and Sparta's persistence (PATCH
 * `{ keys }` to user_health_conditions; "None" = empty array).
 *
 * Reached from Eligible. On Continue it calls goToNextStep({ from:
 * HealthConditions }) — the rules engine advances the static survey to
 * /step/health-goals from there.
 */
export function HealthConditionsScreen() {
  const t = useT();
  const { user } = useRulesEngine();
  const { goToNextStep } = useOnboardingSteps();
  const { conditions, initialSelection, isLoading, error } = useHealthConditions();

  // Local selection state (no form library — no validation, just a key list).
  const [selected, setSelected] = useState<string[]>([]);
  const [noneChecked, setNoneChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [missingOrg, setMissingOrg] = useState(false);
  const missingOrgLoggedRef = useRef(false);

  // Seed the selection from the user's saved conditions once they load.
  useEffect(() => {
    setSelected(initialSelection);
  }, [initialSelection]);

  // The user should have a paying org by this step (eligibility passed). If not,
  // something went wrong during enrollment — log it and surface a contact-support
  // message (Sparta's "Health Conditions step mounted without Paying Org" guard;
  // it shows a toast, which onboarding doesn't have, so this is an inline banner).
  useEffect(() => {
    if (!user) return;
    const hasOrg = Boolean(user.payingOrganization?.resourceUri);
    // Log once, but keep the banner in sync with the current truth so it clears
    // if a later refresh hydrates payingOrganization.
    if (!hasOrg && !missingOrgLoggedRef.current) {
      missingOrgLoggedRef.current = true;
      logError('Health Conditions step mounted without Paying Org.');
    }
    setMissingOrg(!hasOrg);
  }, [user]);

  // Picking any condition clears "None" (mutually exclusive, demo-style: rows
  // auto-deselect rather than disable).
  const toggleCondition = (key: string) => {
    setNoneChecked(false);
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  // "None of these" clears every condition; toggling it off just unchecks it.
  const toggleNone = () => {
    setNoneChecked((prev) => {
      if (!prev) setSelected([]);
      return !prev;
    });
  };

  const onContinue = async () => {
    trackEvent('health-conditions-click-continue', { uuid: user?.uuid });
    setSubmitError(false);

    // Only persist when the selection changed from what loaded — an untouched
    // form just advances, no redundant write. Order-independent set compare;
    // `selected` is already [] when "None of these" is checked.
    const isDirty =
      selected.length !== initialSelection.length ||
      selected.some((key) => !initialSelection.includes(key));
    if (!isDirty) {
      goToNextStep({ from: '/step/health-conditions' });
      return;
    }

    const userUrn = user?.urn;
    if (!userUrn) {
      setSubmitError(true);
      return;
    }
    try {
      setIsSubmitting(true);
      // "None" persists as []; `selected` is already [] when noneChecked, so
      // it's the single source for the payload.
      await updateUserHealthConditions(userUrn, selected);
      goToNextStep({ from: '/step/health-conditions' });
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Screen
      busy={isSubmitting}
      footer={
        <CtaButton
          isBusy={isSubmitting}
          isDisabled={isSubmitting || isLoading}
          onPress={onContinue}
          testID='health-conditions-continue'
        >
          <T _str='Continue' />
        </CtaButton>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading
          title={
            initialSelection.length > 0
              ? t('What conditions do you have?')
              : t('Do you have any of the following conditions?')
          }
          subtitle={
            initialSelection.length > 0
              ? t("We've pre-selected condition(s) from your eligibility info.")
              : undefined
          }
        />

        {missingOrg ? (
          <Text size='sm' className='text-error-700'>
            <T _str='Failed to load your paying org information, something may have occurred during enrollment. Please contact our support teams for assistance.' />
          </Text>
        ) : null}

        {error != null ? (
          <Text size='sm' className='text-error-700'>
            <T _str='We couldn’t load the list of conditions. Please refresh and try again.' />
          </Text>
        ) : null}

        {submitError ? (
          <Text size='sm' className='text-error-700'>
            <T _str='Something went wrong saving your conditions. Please try again.' />
          </Text>
        ) : null}

        {isLoading ? (
          <SelectableRowSkeleton count={6} />
        ) : (
          <VStack space='lg'>
            {conditions.map((condition) => {
              const isSelected = selected.includes(condition.key);
              return (
                <SelectableRow
                  key={condition.key}
                  isSelected={isSelected}
                  onPress={() => toggleCondition(condition.key)}
                  accessibilityLabel={condition.displayName}
                  className='py-6'
                  testID={`health-conditions-${condition.key}`}
                >
                  <CheckboxCardContent label={condition.displayName} isSelected={isSelected} />
                </SelectableRow>
              );
            })}

            <SelectableRow
              isSelected={noneChecked}
              onPress={toggleNone}
              bordered={false}
              accessibilityLabel={t('None of these conditions')}
              className='py-6'
              testID='health-conditions-none'
            >
              <CheckboxCardContent label={t('None of these conditions')} isSelected={noneChecked} />
            </SelectableRow>
          </VStack>
        )}
      </VStack>
    </Screen>
  );
}
